import React, { useState } from 'react';
import {
  CloudUploadOutlined, DescriptionOutlined, ImageOutlined,
  InfoOutlined, PaletteOutlined, PhoneIphoneOutlined, WebOutlined,
} from '@material-ui/icons';
import PremioAPI from '../premio-api';
import useSnackbar from '../../../hooks/useSnackbar';
import { getPremioWebUrl } from '../premio-urls';
import { apiErrorMessage, getConfigValue, setConfigValue } from './editor-model';

export const Field = ({ label, value, onChange, multiline, help, ...props }) => (
  <label className="premio-editor__field">
    <span>{label}</span>
    {help && <span className="premio-editor__help">{help}</span>}
    {multiline
      ? <textarea aria-label={label} rows={3} {...props} value={value ?? ''} onChange={(event) => onChange(event.target.value)} />
      : <input aria-label={label} type="text" {...props} value={value ?? ''} onChange={(event) => onChange(event.target.value)} />}
  </label>
);

const SectionHeader = ({ icon, eyebrow, title, children }) => (
  <div className="announcement-form__section-header">
    <span className="announcement-form__section-icon">{icon}</span>
    <div>
      <span className="announcement-wizard__eyebrow">{eyebrow}</span>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  </div>
);

const assetPreviewUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('/') && !value.startsWith('//')) return `${getPremioWebUrl()}${value}`;
  return '';
};

const AssetPreview = ({ label, value, hero, pdf }) => {
  const [failedUrl, setFailedUrl] = useState('');
  const url = assetPreviewUrl(value);
  if (pdf) return url ? (
    <a className="premio-editor__asset-link" href={url} target="_blank" rel="noopener noreferrer">
      <DescriptionOutlined /> Ver bases del premio
    </a>
  ) : null;
  return (
    <div className={`premio-editor__asset-preview${hero ? ' premio-editor__asset-preview--hero' : ''}`}>
      {url && failedUrl !== url
        ? <img src={url} alt={`Vista previa: ${label}`} onError={() => setFailedUrl(url)} />
        : <><ImageOutlined /><span>{url ? 'No se pudo cargar la imagen. Revisa el enlace o sube un archivo.' : `Vista previa: ${label.toLowerCase()}`}</span></>}
    </div>
  );
};

const AssetField = ({ label, help, value, onChange, pdf, hero, disabled, onUploading = () => {} }) => {
  const { showSnackbar } = useSnackbar();
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const upload = async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    const accepted = pdf ? ['application/pdf'] : ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!accepted.includes(file.type) || file.size > 10 * 1024 * 1024) {
      const message = `${label}: selecciona ${pdf ? 'un PDF' : 'una imagen PNG, JPG, WebP o GIF'} de hasta 10 MB.`;
      setError(message);
      showSnackbar(message, 'warning');
      return;
    }
    setUploading(true);
    onUploading(true);
    try {
      const response = await PremioAPI.uploadConvocatoriaAsset(file);
      onChange(response.data.url);
      showSnackbar(`${label}: archivo subido. Guarda el premio para aplicar el cambio.`, 'success');
    } catch (uploadError) {
      const message = `${label}: ${apiErrorMessage(uploadError)}`;
      setError(message);
      showSnackbar(message, 'error');
    } finally {
      setUploading(false);
      onUploading(false);
    }
  };
  return (
    <div className="premio-editor__asset">
      <Field label={label} help={help} value={value} onChange={onChange} disabled={disabled || uploading} placeholder="Pega un enlace o sube un archivo" />
      <AssetPreview label={label} value={value} hero={hero} pdf={pdf} />
      <label className="premio-editor__upload">
        <CloudUploadOutlined />
        <strong>{uploading ? 'Subiendo archivo…' : pdf ? 'Seleccionar PDF' : 'Seleccionar imagen'}</strong>
        <span>{pdf ? 'PDF' : 'PNG, JPG, WebP o GIF'} · Máximo 10 MB</span>
        <input aria-label={`Subir archivo: ${label}`} type="file" accept={pdf ? '.pdf' : '.png,.jpg,.jpeg,.webp,.gif'} onChange={upload} disabled={disabled || uploading} />
      </label>
      {error && <span className="premio-editor__error" role="alert">{error}</span>}
    </div>
  );
};

const InfoCardsEditor = ({ value, onChange, disabled }) => {
  const cards = value || [];
  const patch = (index, changes) => onChange(cards.map((card, i) => i === index ? { ...card, ...changes } : card));
  return (
    <fieldset className="premio-editor__info-cards" disabled={disabled}>
      {value === undefined ? (
        <button type="button" className="premio-editor__button" onClick={() => onChange([])}>Personalizar tarjetas</button>
      ) : (
        <>
          {cards.length === 0 && <p className="premio-editor__hint">No se mostrarán tarjetas informativas.</p>}
          {cards.map((card, index) => (
            <div className="premio-editor__info-card" key={index}>
              <div className="announcement-form__field-grid announcement-form__field-grid--two">
                <Field label="Título de la tarjeta" value={card.title} onChange={(title) => patch(index, { title })} />
                <label className="premio-editor__field"><span>Estilo</span>
                  <select value={card.variant || 'lista'} onChange={(event) => patch(index, { variant: event.target.value })}>
                    <option value="lista">Lista</option><option value="destacada">Destacada</option>
                  </select>
                </label>
                <label className="premio-editor__field"><span>Ícono</span>
                  <select value={card.icon || ''} onChange={(event) => patch(index, { icon: event.target.value || undefined })}>
                    <option value="">Sin ícono</option><option value="users">Personas</option><option value="medal">Medalla</option><option value="star">Estrella</option>
                  </select>
                </label>
                <Field label="Texto" value={card.text} onChange={(text) => patch(index, { text })} multiline />
                <Field label="Nota al pie" value={card.footnote} onChange={(footnote) => patch(index, { footnote })} />
              </div>
              <label className="premio-editor__checkbox"><input type="checkbox" checked={!!card.show_closing_date} onChange={(event) => patch(index, { show_closing_date: event.target.checked })} />Mostrar fecha de cierre</label>
              {(card.items || []).map((originalItem, itemIndex) => {
                const item = typeof originalItem === 'string' ? { label: originalItem } : originalItem;
                const patchItem = (changes) => patch(index, { items: card.items.map((existing, i) => i === itemIndex ? { ...item, ...changes } : existing) });
                return <div className="announcement-form__field-grid announcement-form__field-grid--two premio-editor__option" key={itemIndex}>
                  <Field label="Elemento" value={item.label} onChange={(label) => patchItem({ label })} />
                  <Field label="Monto" value={item.amount} onChange={(amount) => patchItem({ amount })} />
                  <Field label="Detalle" value={item.detail} onChange={(detail) => patchItem({ detail })} />
                  <button type="button" className="premio-editor__button premio-editor__danger" onClick={() => patch(index, { items: card.items.filter((_, i) => i !== itemIndex) })}>Quitar elemento</button>
                </div>;
              })}
              <div className="premio-editor__actions">
                <button type="button" className="premio-editor__button" onClick={() => patch(index, { items: [...(card.items || []), { label: '' }] })}>Agregar elemento</button>
                <button type="button" className="premio-editor__button premio-editor__danger" onClick={() => onChange(cards.filter((_, i) => i !== index))}>Quitar tarjeta</button>
              </div>
            </div>
          ))}
          <button type="button" className="premio-editor__button" onClick={() => onChange([...cards, { title: '', text: '', variant: 'lista' }])}>Agregar tarjeta</button>
        </>
      )}
    </fieldset>
  );
};

const ColorField = ({ label, value, onChange, help, disabled }) => (
  <div className="premio-editor__field">
    <span>{label}</span>
    <div className="premio-editor__color-group">
      <span aria-hidden="true">#</span>
      <input
        aria-label={label}
        value={(value || '').replace(/^#/, '')}
        onChange={(event) => {
          const hex = event.target.value.replace(/^#/, '');
          onChange(hex ? `#${hex}` : '');
        }}
        placeholder="003965"
        maxLength={6}
        pattern="[0-9a-fA-F]{6}"
        title="Ingresa seis caracteres hexadecimales. Por ejemplo: 38BDF8."
        disabled={disabled}
        spellCheck={false}
      />
      <input
        type="color"
        aria-label={`Seleccionar ${label.toLowerCase()}`}
        value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#003965'}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
    </div>
    <span className="premio-editor__help premio-editor__color-help"><InfoOutlined />{help}</span>
  </div>
);

const BrandingEditor = ({ value, onChange, disabled, onUploading, step = 'login' }) => {
  const change = (path, next) => onChange(setConfigValue(value, path, next));
  const textField = (path, label, help, multiline = false) => (
    <Field key={path} label={label} help={help} value={getConfigValue(value, path)} onChange={(next) => change(path, next)} multiline={multiline} disabled={disabled} />
  );
  if (step === 'identity') return (
    <>
      <section className="announcement-form__card">
        <SectionHeader icon={<PaletteOutlined />} eyebrow="Identidad del premio" title="Colores y tipografía">
          Personaliza la apariencia del premio con los colores y la tipografía del programa.
        </SectionHeader>
        <div className="announcement-form__subsection">
          <div className="announcement-form__subsection-heading">
            <span className="announcement-wizard__eyebrow">Paleta del acceso</span>
            <strong>Colores del premio</strong>
          </div>
          <div className="announcement-form__field-grid announcement-form__field-grid--two">
            <ColorField label="Color principal" value={value.primary_color} onChange={(next) => change('primary_color', next)} help="Se aplica a los botones y acentos principales del premio." disabled={disabled} />
            <ColorField label="Color secundario" value={value.secondary_color} onChange={(next) => change('secondary_color', next)} help="Se aplica a los enlaces e indicadores secundarios del premio." disabled={disabled} />
          </div>
        </div>
        <div className="announcement-form__field-grid announcement-form__field-grid--two">
          {textField('font_family', 'Tipografía del premio', 'Nombre de la fuente que usará el programa. Por ejemplo: Arial.')}
          {textField('logo_height', 'Altura del logo del encabezado', 'Tamaño del logo durante la postulación. Por ejemplo: 40px.')}
        </div>
      </section>
      <section className="announcement-form__card">
        <SectionHeader icon={<ImageOutlined />} eyebrow="Imagen del programa" title="Logos y portada">
          Define los logos del premio y la imagen que acompaña el inicio de sesión.
        </SectionHeader>
        <div className="announcement-form__field-grid announcement-form__field-grid--two">
          <AssetField label="Logo del programa" help="Se muestra en el encabezado durante la postulación." value={value.logo_url} onChange={(next) => change('logo_url', next)} disabled={disabled} onUploading={onUploading} />
          <AssetField label="Logo para el inicio de sesión" help="Se muestra sobre el título de bienvenida." value={value.login_logo_url} onChange={(next) => change('login_logo_url', next)} disabled={disabled} onUploading={onUploading} />
        </div>
        <AssetField label="Imagen de portada" help="Imagen lateral de la pantalla de inicio de sesión en computadores." value={value.hero_image_url} onChange={(next) => change('hero_image_url', next)} hero disabled={disabled} onUploading={onUploading} />
      </section>
    </>
  );
  return (
    <>
      <section className="announcement-form__card">
        <SectionHeader icon={<WebOutlined />} eyebrow="Contenido del acceso" title="Inicio de sesión">
          Personaliza el nombre del programa y los textos que verán quienes postulen al premio.
        </SectionHeader>
        <div className="announcement-form__field-grid">
          {textField('visible_title', 'Nombre visible del programa', 'Nombre público del premio en el acceso y la postulación.')}
          {textField('description', 'Descripción del programa', 'Presentación breve del programa en el listado de premios.', true)}
          {textField('login.title', 'Título de la página de inicio', 'Llamado principal que aparece debajo del logo.')}
          {textField('login.subtitle', 'Texto para iniciar sesión', 'Instrucción que acompaña al botón de acceso con Google.')}
        </div>
      </section>
      <section className="announcement-form__card">
        <SectionHeader icon={<DescriptionOutlined />} eyebrow="Información para postular" title="Bases y términos del premio">
          Agrega las bases y el enlace a los términos que aparecen al iniciar sesión.
        </SectionHeader>
        <div className="announcement-form__field-grid">
          {textField('login.terms_text', 'Texto de aceptación de términos', 'Texto que acompaña al enlace de términos y privacidad.', true)}
          {textField('login.terms_url', 'Enlace a términos y privacidad')}
          {textField('login.bases_text', 'Texto del enlace a las bases', 'Texto que acompaña al enlace para consultar las bases.')}
          <AssetField label="Bases del premio" help="Sube las bases en PDF o pega el enlace al documento." value={value.bases_url} onChange={(next) => change('bases_url', next)} pdf disabled={disabled} onUploading={onUploading} />
        </div>
      </section>
      <section className="announcement-form__card">
        <SectionHeader icon={<PhoneIphoneOutlined />} eyebrow="Vista en celulares" title="Tarjetas informativas">
          Información opcional que aparece debajo del inicio de sesión en celulares.
        </SectionHeader>
        <InfoCardsEditor value={value.login?.info_cards} onChange={(cards) => change('login.info_cards', cards)} disabled={disabled} />
      </section>
    </>
  );
};

export default BrandingEditor;
